// src/scenes/MenuScene.js
// 🎮 Enhanced MenuScene - FIXED: Mobile responsive with virtual keyboard support
// Full height/width responsive design with mobile input handling

import Phaser from 'phaser';
import { gameState } from '../game/state.js';
import { GAME_CONFIG, CHARACTER_TYPES } from '../game/config.js';
import SpriteFactory from '../sprites/SpriteFactory.js';

export default class MenuScene extends Phaser.Scene {
  constructor() {
    super({ key: 'MenuScene' });
    this.spriteFactory = null;
    this.selectedCharacter = null;
    this.playerName = '';
    this.isTyping = false;
    this.nameText = null;
    this.cursor = null;
    this.characterCards = {};
    this.virtualKeyboard = null;
    this.inputField = null;
    this.isMobile = false;
  }

  preload() {
    /* console.log('🎨 MenuScene: Creating 90s pixel art...'); */
    
    // Create sprite factory and generate all sprites
    this.spriteFactory = new SpriteFactory(this);
    this.spriteFactory.createAllSprites();
  }

  create() {
    const { width, height } = this.scale;
    
    // Detect mobile device
    this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
                   'ontouchstart' in window;
    
   /*  console.log(`🎮 MenuScene: Creating responsive wedding menu (${width}x${height}, mobile: ${this.isMobile})`); */

    // Create beautiful 90s background
    this.createResponsiveBackground();
    
    // Create floating decorations
    this.createDecorations();
    
    // Main title (Mario-style) - RESPONSIVE
    this.createResponsiveTitle();
    
    // Character selection - RESPONSIVE
    this.createResponsiveCharacterSelection();
    
    // Name input - MOBILE FRIENDLY
    this.createMobileNameInput();
    
    // Menu buttons - RESPONSIVE
    this.createResponsiveMenuButtons();
    
    // Start background animations
    this.startAnimations();
    
    // Setup resize handler
    this.scale.on('resize', this.handleResize, this);
  }

  createResponsiveBackground() {
    const { width, height } = this.scale;
    
    // Mario blue sky background - FULL COVERAGE
    this.add.rectangle(width/2, height/2, width, height, GAME_CONFIG.SPRITES.COLORS.MARIO_BLUE);
    
    // Add clouds (Mario-style) - RESPONSIVE
    const cloudCount = Math.min(8, Math.floor(width / 120));
    for (let i = 0; i < cloudCount; i++) {
      const cloud = this.add.image(
        (i * (width / cloudCount)) + 50, 
        height * 0.08 + Math.sin(i) * (height * 0.04), 
        'cloud'
      ).setScale(Math.min(4, width / 200));
      
      // Slow cloud movement
      this.tweens.add({
        targets: cloud,
        x: cloud.x + 30,
        duration: 8000 + (i * 1000),
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut'
      });
    }
  }

  createDecorations() {
    const { width, height } = this.scale;
    
    // Floating hearts - RESPONSIVE
    const heartCount = Math.min(12, Math.floor((width * height) / 8000));
    for (let i = 0; i < heartCount; i++) {
      const heart = this.add.image(
        Math.random() * width,
        Math.random() * height,
        'heart'
      ).setScale(1.5 + Math.random()).setAlpha(0.7);
      
      // Floating animation
      this.tweens.add({
        targets: heart,
        y: heart.y - 30,
        duration: 4000 + Math.random() * 2000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut'
      });
    }
    
    // Wedding bells in corners - RESPONSIVE
    const bellScale = Math.min(4, width / 200);
    const bell1 = this.add.image(width * 0.08, height * 0.1, 'bell').setScale(bellScale);
    const bell2 = this.add.image(width * 0.92, height * 0.1, 'bell').setScale(bellScale);
    
    // Gentle bell sway
    this.tweens.add({
      targets: [bell1, bell2],
      rotation: 0.1,
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
  }

  createResponsiveTitle() {
    const { width, height } = this.scale;
    
    // RESPONSIVE FONT SIZES
    const titleSize = Math.min(48, width / 16);
    const subtitleSize = Math.min(28, width / 28);
    const competitionSize = Math.min(16, width / 50);
    
    // Main title - RESPONSIVE
    const title = this.add.text(width/2, height * 0.12, '🤵 PABLO & BETTINA\'S 👰', {
      fontSize: `${titleSize}px`,
      fontFamily: 'Arial, sans-serif',
      fontWeight: 'bold',
      color: '#FFFFFF',
      stroke: '#000000',
      strokeThickness: Math.max(2, titleSize / 12),
      shadow: { offsetX: 3, offsetY: 3, color: '#000000', blur: 0, fill: true },
      align: 'center',
      wordWrap: { width: width * 0.9 }
    }).setOrigin(0.5);
    
    const subtitle = this.add.text(width/2, height * 0.2, 'WEDDING GAME', {
      fontSize: `${subtitleSize}px`,
      fontFamily: 'Arial, sans-serif',
      fontWeight: 'bold',
      color: '#FFD700',
      stroke: '#000000',
      strokeThickness: Math.max(2, subtitleSize / 10),
      shadow: { offsetX: 3, offsetY: 3, color: '#000000', blur: 0, fill: true },
      align: 'center'
    }).setOrigin(0.5);
    
    // Bouncing title animation (Mario-style)
    this.tweens.add({
      targets: [title, subtitle],
      scaleY: 1.05,
      duration: 1500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
    
    // Competition subtitle - RESPONSIVE
    this.add.text(width/2, height * 0.28, '🏆 WEDDING GUEST CHAMPIONSHIP 🏆', {
      fontSize: `${competitionSize}px`,
      fontFamily: 'Arial, sans-serif',
      fontWeight: 'bold', 
      color: '#FFFFFF',
      stroke: '#000000',
      strokeThickness: Math.max(1, competitionSize / 8),
      align: 'center',
      wordWrap: { width: width * 0.9 }
    }).setOrigin(0.5);
  }

  createResponsiveCharacterSelection() {
    const { width, height } = this.scale;
    
    // Character selection title - RESPONSIVE
    const selectionSize = Math.min(16, width / 50);
    this.add.text(width/2, height * 0.38, 'CHOOSE YOUR CHARACTER:', {
      fontSize: `${selectionSize}px`,
      fontFamily: 'Arial, sans-serif',
      fontWeight: 'bold',
      color: '#FFFFFF', 
      stroke: '#000000',
      strokeThickness: Math.max(1, selectionSize / 5),
      align: 'center'
    }).setOrigin(0.5);
    
    // Create responsive character cards
    const cardSpacing = Math.min(width * 0.4, 300);
    this.createResponsiveCharacterCard('bride', width/2 - cardSpacing/2, height * 0.55);
    this.createResponsiveCharacterCard('groom', width/2 + cardSpacing/2, height * 0.55);
  }

  createResponsiveCharacterCard(character, x, y) {
    const { width, height } = this.scale;
    const isBride = character === 'bride';
    const cardColor = isBride ? GAME_CONFIG.SPRITES.COLORS.BRIDE_PINK : GAME_CONFIG.SPRITES.COLORS.GROOM_BLUE;
    const characterName = isBride ? 'BETTINA' : 'PABLO';
    const spriteKey = character;
    
    // RESPONSIVE CARD SIZE
    const cardWidth = Math.min(160, width * 0.35);
    const cardHeight = Math.min(180, height * 0.25);
    const nameSize = Math.min(18, width / 44);
    
    // Card background - RESPONSIVE
    const cardBg = this.add.graphics()
      .fillStyle(cardColor)
      .fillRoundedRect(x - cardWidth/2, y - cardHeight/2, cardWidth, cardHeight, 12)
      .lineStyle(4, GAME_CONFIG.SPRITES.COLORS.UI_BLACK)
      .strokeRoundedRect(x - cardWidth/2, y - cardHeight/2, cardWidth, cardHeight, 12);
    
    // Character sprite - RESPONSIVE SCALING
    const spriteScale = Math.min(3, width / 267);
    const characterSprite = this.add.image(x, y - cardHeight * 0.1, spriteKey).setScale(spriteScale);
    
    // Character name - RESPONSIVE
    const nameText = this.add.text(x, y + cardHeight * 0.25, characterName, {
      fontSize: `${nameSize}px`,
      fontFamily: 'Arial, sans-serif',
      fontWeight: 'bold',
      color: '#FFFFFF',
      stroke: '#000000',
      strokeThickness: Math.max(1, nameSize / 6),
      align: 'center'
    }).setOrigin(0.5);
    
    // Store card elements for easy access
    this.characterCards[character] = {
      background: cardBg,
      sprite: characterSprite,
      nameText: nameText,
      x: x,
      y: y,
      color: cardColor,
      width: cardWidth,
      height: cardHeight
    };
    
    // Make interactive - TOUCH FRIENDLY
    const hitArea = this.add.rectangle(x, y, cardWidth, cardHeight, 0x000000, 0)
      .setInteractive()
      .on('pointerover', () => {
        // ONLY change the border, keep the fill the same
        cardBg.clear()
          .fillStyle(cardColor)
          .fillRoundedRect(x - cardWidth/2, y - cardHeight/2, cardWidth, cardHeight, 12)
          .lineStyle(6, GAME_CONFIG.SPRITES.COLORS.WEDDING_GOLD)
          .strokeRoundedRect(x - cardWidth/2, y - cardHeight/2, cardWidth, cardHeight, 12);
      })
      .on('pointerout', () => {
        if (this.selectedCharacter !== character) {
          cardBg.clear()
            .fillStyle(cardColor)
            .fillRoundedRect(x - cardWidth/2, y - cardHeight/2, cardWidth, cardHeight, 12)
            .lineStyle(4, GAME_CONFIG.SPRITES.COLORS.UI_BLACK)
            .strokeRoundedRect(x - cardWidth/2, y - cardHeight/2, cardWidth, cardHeight, 12);
        }
      })
      .on('pointerdown', () => {
        this.selectCharacter(character);
      });
  }

  selectCharacter(character) {
    /* console.log(`👤 Selected character: ${character}`); */
    
    // Clear previous selection
    if (this.selectedCharacter && this.characterCards[this.selectedCharacter]) {
      const prevCard = this.characterCards[this.selectedCharacter];
      prevCard.background.clear()
        .fillStyle(prevCard.color)
        .fillRoundedRect(prevCard.x - prevCard.width/2, prevCard.y - prevCard.height/2, prevCard.width, prevCard.height, 12)
        .lineStyle(4, GAME_CONFIG.SPRITES.COLORS.UI_BLACK)
        .strokeRoundedRect(prevCard.x - prevCard.width/2, prevCard.y - prevCard.height/2, prevCard.width, prevCard.height, 12);
    }
    
    // Set new selection
    this.selectedCharacter = character;
    const card = this.characterCards[character];
    
    // Highlight selected card with green border
    card.background.clear()
      .fillStyle(card.color)
      .fillRoundedRect(card.x - card.width/2, card.y - card.height/2, card.width, card.height, 12)
      .lineStyle(6, GAME_CONFIG.SPRITES.COLORS.MARIO_GREEN)
      .strokeRoundedRect(card.x - card.width/2, card.y - card.height/2, card.width, card.height, 12);
    
    // Show selection feedback - RESPONSIVE
    const checkmarkSize = Math.min(32, this.scale.width / 25);
    const checkmark = this.add.text(card.x, card.y - card.height * 0.3, '✓', {
      fontSize: `${checkmarkSize}px`,
      fontFamily: 'Arial, sans-serif',
      fontWeight: 'bold',
      color: '#32CD32'
    }).setOrigin(0.5);
    
    this.tweens.add({
      targets: checkmark,
      alpha: 0,
      scale: 2,
      duration: 1500,
      onComplete: () => checkmark.destroy()
    });
    
    this.checkCanStart();
  }

  createMobileNameInput() {
    const { width, height } = this.scale;
    
    // Name input title - RESPONSIVE
    const inputTitleSize = Math.min(18, width / 44);
    this.add.text(width/2, height * 0.72, 'ENTER YOUR NAME:', {
      fontSize: `${inputTitleSize}px`,
      fontFamily: 'Arial, sans-serif',
      fontWeight: 'bold',
      color: '#FFFFFF',
      stroke: '#000000', 
      strokeThickness: Math.max(1, inputTitleSize / 6),
      align: 'center'
    }).setOrigin(0.5);
    
    // MOBILE-FRIENDLY INPUT
    if (this.isMobile) {
      this.createMobileVirtualKeyboard();
    } else {
      this.createDesktopInput();
    }
  }

  createMobileVirtualKeyboard() {
    const { width, height } = this.scale;
    
    // Input field background - RESPONSIVE
    const inputWidth = Math.min(300, width * 0.8);
    const inputHeight = Math.min(40, height * 0.06);
    
    const inputBg = this.add.graphics()
      .fillStyle(GAME_CONFIG.SPRITES.COLORS.UI_LIGHT_GRAY)
      .fillRoundedRect(width/2 - inputWidth/2, height * 0.78 - inputHeight/2, inputWidth, inputHeight, 6)
      .lineStyle(3, GAME_CONFIG.SPRITES.COLORS.UI_DARK_GRAY)
      .strokeRoundedRect(width/2 - inputWidth/2, height * 0.78 - inputHeight/2, inputWidth, inputHeight, 6);
    
    // Name text display - RESPONSIVE
    const nameSize = Math.min(16, width / 50);
    this.nameText = this.add.text(width/2, height * 0.78, 'TAP TO ENTER NAME', {
      fontSize: `${nameSize}px`,
      fontFamily: 'Arial, sans-serif',
      fontWeight: 'bold',
      color: '#808080',
      align: 'center'
    }).setOrigin(0.5);
    
    // Make input area tappable
    const inputArea = this.add.rectangle(width/2, height * 0.78, inputWidth, inputHeight, 0x000000, 0)
      .setInteractive()
      .on('pointerdown', () => {
        if (this.isMobile) {
          this.showVirtualKeyboard();
        } else {
          this.startTyping();
        }
      });

    // Create virtual keyboard for mobile
    this.createVirtualKeyboard();
  }

  createDesktopInput() {
    const { width, height } = this.scale;
    
    // Input field background - RESPONSIVE
    const inputWidth = Math.min(300, width * 0.8);
    const inputHeight = Math.min(40, height * 0.06);
    
    const inputBg = this.add.graphics()
      .fillStyle(GAME_CONFIG.SPRITES.COLORS.UI_LIGHT_GRAY)
      .fillRoundedRect(width/2 - inputWidth/2, height * 0.78 - inputHeight/2, inputWidth, inputHeight, 6)
      .lineStyle(3, GAME_CONFIG.SPRITES.COLORS.UI_DARK_GRAY)
      .strokeRoundedRect(width/2 - inputWidth/2, height * 0.78 - inputHeight/2, inputWidth, inputHeight, 6);
    
    // Make input interactive
    inputBg.setInteractive(new Phaser.Geom.Rectangle(width/2 - inputWidth/2, height * 0.78 - inputHeight/2, inputWidth, inputHeight), Phaser.Geom.Rectangle.Contains)
      .on('pointerdown', () => this.startTyping());
    
    // Name text display - RESPONSIVE
    const nameSize = Math.min(16, width / 50);
    this.nameText = this.add.text(width/2, height * 0.78, 'CLICK TO TYPE NAME', {
      fontSize: `${nameSize}px`,
      fontFamily: 'Arial, sans-serif',
      fontWeight: 'bold',
      color: '#808080',
      align: 'center'
    }).setOrigin(0.5);
    
    // Cursor - RESPONSIVE
    this.cursor = this.add.text(width/2 + inputWidth/3, height * 0.78, '|', {
      fontSize: `${nameSize}px`,
      fontFamily: 'Arial, sans-serif',
      fontWeight: 'bold',
      color: '#000000'
    }).setOrigin(0.5).setVisible(false);
    
    // Blinking cursor
    this.cursorTween = this.tweens.add({
      targets: this.cursor,
      alpha: 0,
      duration: 500,
      yoyo: true,
      repeat: -1,
      paused: true
    });
    
    this.setupKeyboard();
  }

  createVirtualKeyboard() {
    const { width, height } = this.scale;
    
    // Virtual keyboard container (initially hidden)
    this.virtualKeyboard = this.add.container(0, 0);
    this.virtualKeyboard.setVisible(false);
    
    // Keyboard background
    const keyboardBg = this.add.graphics();
    keyboardBg.fillStyle(0x2C3E50, 0.95);
    keyboardBg.fillRect(0, height * 0.4, width, height * 0.6);
    this.virtualKeyboard.add(keyboardBg);
    
    // Keyboard title
    const kbTitleSize = Math.min(20, width / 40);
    const keyboardTitle = this.add.text(width/2, height * 0.42, 'ENTER YOUR NAME', {
      fontSize: `${kbTitleSize}px`,
      fontFamily: 'Arial, sans-serif',
      fontWeight: 'bold',
      color: '#FFFFFF',
      align: 'center'
    }).setOrigin(0.5);
    this.virtualKeyboard.add(keyboardTitle);
    
    // Input display on keyboard
    const inputDisplaySize = Math.min(24, width / 33);
    this.keyboardInput = this.add.text(width/2, height * 0.48, '', {
      fontSize: `${inputDisplaySize}px`,
      fontFamily: 'Arial, sans-serif',
      fontWeight: 'bold',
      color: '#FFD700',
      stroke: '#000000',
      strokeThickness: 2,
      align: 'center'
    }).setOrigin(0.5);
    this.virtualKeyboard.add(this.keyboardInput);
    
    // Create keyboard keys
    this.createKeyboardKeys();
  }

  createKeyboardKeys() {
    const { width, height } = this.scale;
    
    // Keyboard layout
    const rows = [
      ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
      ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
      ['Z', 'X', 'C', 'V', 'B', 'N', 'M']
    ];
    
    const keySize = Math.min(40, width / 20);
    const keySpacing = keySize + 4;
    const startY = height * 0.55;
    
    rows.forEach((row, rowIndex) => {
      const rowWidth = row.length * keySpacing;
      const startX = (width - rowWidth) / 2 + keySpacing / 2;
      
      row.forEach((letter, colIndex) => {
        const keyX = startX + (colIndex * keySpacing);
        const keyY = startY + (rowIndex * keySpacing);
        
        this.createKey(letter, keyX, keyY, keySize);
      });
    });
    
    // Special keys
    const specialKeyY = startY + (3 * keySpacing);
    
    // Space bar
    this.createKey('SPACE', width/2, specialKeyY, keySize * 3, 'space');
    
    // Backspace
    this.createKey('←', width * 0.8, specialKeyY, keySize * 1.5, 'backspace');
    
    // Done button
    this.createKey('DONE', width * 0.2, specialKeyY, keySize * 1.5, 'done');
  }

  createKey(label, x, y, keyWidth, action = null) {
    const keyHeight = Math.min(35, this.scale.width / 23);
    
    // Key background
    const keyBg = this.add.graphics();
    keyBg.fillStyle(0x34495E);
    keyBg.fillRoundedRect(x - keyWidth/2, y - keyHeight/2, keyWidth, keyHeight, 5);
    keyBg.lineStyle(2, 0x5D6D7E);
    keyBg.strokeRoundedRect(x - keyWidth/2, y - keyHeight/2, keyWidth, keyHeight, 5);
    this.virtualKeyboard.add(keyBg);
    
    // Key text
    const keySize = Math.min(16, this.scale.width / 50);
    const keyText = this.add.text(x, y, label, {
      fontSize: `${keySize}px`,
      fontFamily: 'Arial, sans-serif',
      fontWeight: 'bold',
      color: '#FFFFFF',
      align: 'center'
    }).setOrigin(0.5);
    this.virtualKeyboard.add(keyText);
    
    // Make key interactive
    const keyButton = this.add.rectangle(x, y, keyWidth, keyHeight, 0x000000, 0)
      .setInteractive()
      .on('pointerdown', () => {
        // Key press animation
        this.tweens.add({
          targets: [keyBg, keyText],
          scaleX: 0.95,
          scaleY: 0.95,
          duration: 100,
          yoyo: true
        });
        
        // Handle key press
        this.handleVirtualKeyPress(label, action);
      });
    this.virtualKeyboard.add(keyButton);
  }

  handleVirtualKeyPress(key, action) {
    switch (action) {
      case 'space':
        if (this.playerName.length < 15) {
          this.playerName += ' ';
        }
        break;
      case 'backspace':
        this.playerName = this.playerName.slice(0, -1);
        break;
      case 'done':
        this.hideVirtualKeyboard();
        return;
      default:
        if (this.playerName.length < 15) {
          this.playerName += key;
        }
    }
    
    this.updateVirtualKeyboardDisplay();
    this.checkCanStart();
  }

  updateVirtualKeyboardDisplay() {
    if (this.keyboardInput) {
      this.keyboardInput.setText(this.playerName || 'Enter name...');
    }
    if (this.nameText) {
      this.nameText.setText(this.playerName || 'TAP TO ENTER NAME');
      this.nameText.setStyle({ 
        color: this.playerName ? '#000000' : '#808080',
        fontSize: `${Math.min(16, this.scale.width / 50)}px`
      });
    }
  }

  showVirtualKeyboard() {
    if (this.virtualKeyboard) {
      this.virtualKeyboard.setVisible(true);
      this.isTyping = true;
      this.updateVirtualKeyboardDisplay();
      
      // Animate keyboard in
      this.tweens.add({
        targets: this.virtualKeyboard,
        y: 0,
        duration: 300,
        ease: 'Power2'
      });
    }
  }

  hideVirtualKeyboard() {
    if (this.virtualKeyboard) {
      // Animate keyboard out
      this.tweens.add({
        targets: this.virtualKeyboard,
        y: this.scale.height * 0.6,
        duration: 300,
        ease: 'Power2',
        onComplete: () => {
          this.virtualKeyboard.setVisible(false);
          this.isTyping = false;
          this.finishTyping();
        }
      });
    }
  }

  setupKeyboard() {
    this.input.keyboard.on('keydown', (event) => {
      if (!this.isTyping) return;
      
      if (event.key === 'Enter') {
        this.finishTyping();
      } else if (event.key === 'Backspace') {
        this.playerName = this.playerName.slice(0, -1);
        this.updateNameDisplay();
        this.checkCanStart();
      } else if (event.key.length === 1 && this.playerName.length < 15) {
        if (/[a-zA-Z0-9\s]/.test(event.key)) {
          this.playerName += event.key.toUpperCase();
          this.updateNameDisplay();
          this.checkCanStart();
        }
      }
    });
  }

  startTyping() {
    this.isTyping = true;
    if (this.cursor) {
      this.cursor.setVisible(true);
      this.cursorTween.resume();
    }
    const nameSize = Math.min(16, this.scale.width / 50);
    this.nameText.setStyle({ 
      color: '#000000', 
      fontSize: `${nameSize}px`, 
      fontFamily: 'Arial, sans-serif', 
      fontWeight: 'bold' 
    });
    this.updateNameDisplay();
  }

  finishTyping() {
    this.isTyping = false;
    if (this.cursor) {
      this.cursor.setVisible(false);
      this.cursorTween.pause();
    }
    
    const nameSize = Math.min(16, this.scale.width / 50);
    if (this.playerName.trim()) {
      this.nameText.setText(`WELCOME ${this.playerName.trim()}!`);
      this.nameText.setStyle({ 
        color: '#32CD32', 
        fontSize: `${nameSize}px`, 
        fontFamily: 'Arial, sans-serif', 
        fontWeight: 'bold' 
      });
    } else {
      this.nameText.setText(this.isMobile ? 'TAP TO ENTER NAME' : 'CLICK TO TYPE NAME');
      this.nameText.setStyle({ 
        color: '#808080', 
        fontSize: `${nameSize}px`, 
        fontFamily: 'Arial, sans-serif', 
        fontWeight: 'bold' 
      });
    }
    
    this.checkCanStart();
  }

  updateNameDisplay() {
    const nameSize = Math.min(16, this.scale.width / 50);
    if (this.playerName) {
      this.nameText.setText(this.playerName);
      this.nameText.setStyle({ 
        color: '#000000', 
        fontSize: `${nameSize}px`, 
        fontFamily: 'Arial, sans-serif', 
        fontWeight: 'bold' 
      });
      if (this.cursor) {
        this.cursor.x = this.nameText.x + (this.nameText.width / 2) + 10;
      }
    } else {
      this.nameText.setText('');
      if (this.cursor) {
        this.cursor.x = this.nameText.x;
      }
    }
  }

  createResponsiveMenuButtons() {
    const { width, height } = this.scale;
    
    // RESPONSIVE BUTTON SIZES
    const buttonWidth = Math.min(200, width * 0.5);
    const buttonHeight = Math.min(40, height * 0.06);
    const buttonTextSize = Math.min(18, width / 44);
    
    // Start button (disabled initially) - RESPONSIVE
    this.startButton = this.add.graphics()
      .fillStyle(GAME_CONFIG.SPRITES.COLORS.UI_GRAY)
      .fillRoundedRect(width/2 - buttonWidth/2, height * 0.88 - buttonHeight/2, buttonWidth, buttonHeight, 10)
      .lineStyle(4, GAME_CONFIG.SPRITES.COLORS.UI_DARK_GRAY)
      .strokeRoundedRect(width/2 - buttonWidth/2, height * 0.88 - buttonHeight/2, buttonWidth, buttonHeight, 10);
    
    this.startButtonText = this.add.text(width/2, height * 0.88, 'START GAME', {
      fontSize: `${buttonTextSize}px`,
      fontFamily: 'Arial, sans-serif',
      fontWeight: 'bold',
      color: '#808080',
      align: 'center'
    }).setOrigin(0.5);
    
    // Scoreboard button - RESPONSIVE
    const scoreboardWidth = Math.min(120, width * 0.3);
    const scoreboardHeight = Math.min(35, height * 0.05);
    const scoreboardTextSize = Math.min(14, width / 57);
    
    const scoreboardBtn = this.add.graphics()
      .fillStyle(GAME_CONFIG.SPRITES.COLORS.MARIO_YELLOW)
      .fillRoundedRect(width - scoreboardWidth - 20, height - scoreboardHeight - 15, scoreboardWidth, scoreboardHeight, 6)
      .lineStyle(3, GAME_CONFIG.SPRITES.COLORS.UI_BLACK)
      .strokeRoundedRect(width - scoreboardWidth - 20, height - scoreboardHeight - 15, scoreboardWidth, scoreboardHeight, 6)
      .setInteractive(new Phaser.Geom.Rectangle(width - scoreboardWidth - 20, height - scoreboardHeight - 15, scoreboardWidth, scoreboardHeight), Phaser.Geom.Rectangle.Contains)
      .on('pointerdown', () => {
        this.scene.start('ScoreboardScene', { returnTo: 'MenuScene' });
      });
    
    this.add.text(width - scoreboardWidth/2 - 20, height - scoreboardHeight/2 - 15, 'SCORES', {
      fontSize: `${scoreboardTextSize}px`,
      fontFamily: 'Arial, sans-serif',
      fontWeight: 'bold',
      color: '#000000',
      align: 'center'
    }).setOrigin(0.5);
  }

  checkCanStart() {
    const trimmedName = this.playerName.trim();
    /* console.log(`🔍 Checking start conditions - Name: "${trimmedName}" (length: ${trimmedName.length}), Character: "${this.selectedCharacter}"`); */
    
    if (trimmedName.length > 0 && this.selectedCharacter) {
      /* console.log('✅ Both conditions met - enabling start button'); */
      
      const { width, height } = this.scale;
      const buttonWidth = Math.min(200, width * 0.5);
      const buttonHeight = Math.min(40, height * 0.06);
      const buttonTextSize = Math.min(18, width / 44);
      
      // Enable start button
      this.startButton.clear()
        .fillStyle(GAME_CONFIG.SPRITES.COLORS.MARIO_GREEN)
        .fillRoundedRect(width/2 - buttonWidth/2, height * 0.88 - buttonHeight/2, buttonWidth, buttonHeight, 10)
        .lineStyle(4, GAME_CONFIG.SPRITES.COLORS.UI_BLACK)
        .strokeRoundedRect(width/2 - buttonWidth/2, height * 0.88 - buttonHeight/2, buttonWidth, buttonHeight, 10)
        .setInteractive(new Phaser.Geom.Rectangle(width/2 - buttonWidth/2, height * 0.88 - buttonHeight/2, buttonWidth, buttonHeight), Phaser.Geom.Rectangle.Contains)
        .on('pointerdown', () => this.startGame());
      
      this.startButtonText.setStyle({ 
        color: '#FFFFFF', 
        fontSize: `${buttonTextSize}px`, 
        fontFamily: 'Arial, sans-serif', 
        fontWeight: 'bold' 
      });
    } else {
      /* console.log('❌ Conditions not met - button stays disabled'); */
      
      const { width, height } = this.scale;
      const buttonWidth = Math.min(200, width * 0.5);
      const buttonHeight = Math.min(40, height * 0.06);
      const buttonTextSize = Math.min(18, width / 44);
      
      // Keep button disabled
      this.startButton.clear()
        .fillStyle(GAME_CONFIG.SPRITES.COLORS.UI_GRAY)
        .fillRoundedRect(width/2 - buttonWidth/2, height * 0.88 - buttonHeight/2, buttonWidth, buttonHeight, 10)
        .lineStyle(4, GAME_CONFIG.SPRITES.COLORS.UI_DARK_GRAY)
        .strokeRoundedRect(width/2 - buttonWidth/2, height * 0.88 - buttonHeight/2, buttonWidth, buttonHeight, 10);
      
      this.startButtonText.setStyle({ 
        color: '#808080', 
        fontSize: `${buttonTextSize}px`, 
        fontFamily: 'Arial, sans-serif', 
        fontWeight: 'bold' 
      });
    }
  }

  startAnimations() {
    // Add some gentle floating animation to decorative elements
    const elements = this.children.list.filter(child => 
      child.type === 'Image' && (child.texture.key === 'heart' || child.texture.key === 'bell')
    );
    
    elements.forEach((element, index) => {
      this.tweens.add({
        targets: element,
        y: element.y + 3,
        duration: 3000 + (index * 100),
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut'
      });
    });
  }

  handleResize(gameSize) {
    const { width, height } = gameSize;
    /* console.log(`📱 Menu scene resized to ${width}x${height}`); */
    
    // Update mobile detection
    this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
                   'ontouchstart' in window || width < 768;
    
    // Note: In a full implementation, you'd recreate UI elements here
    // For now, the responsive design handles most cases
  }

  startGame() {
    const trimmedName = this.playerName.trim();
    if (!trimmedName || !this.selectedCharacter) return;
    
    /* console.log(`🚀 Starting game: ${trimmedName} as ${this.selectedCharacter}`); */
    
    // Hide virtual keyboard if visible
    if (this.virtualKeyboard && this.virtualKeyboard.visible) {
      this.hideVirtualKeyboard();
    }
    
    // Set game state
    gameState.setPlayer(trimmedName, this.selectedCharacter.toUpperCase());
    gameState.startLevel(1);
    
    // Transition effect
    this.cameras.main.fadeOut(500, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('GameScene');
    });
  }
}